# Voice walkthrough tips

`cairn record` listens while you describe a flow, transcribes it, and asks an LLM to extract a `Flow` YAML. The model is good but not telepathic. These tips get you a usable graph on the first take.

## 1. Name the screen before you name the action

The transcript is the model's only memory. Say what you're looking at before you say what you do:

> "On the **sign-in screen**, I type my email into the email field."

Not:

> "I type my email."

The first form gives the model the node identity (`sign-in`); the second gives it nothing.

## 2. Speak in user actions, not implementation

> "I click the **Sign in** button."

Not:

> "The form submits a POST request to `/api/auth/login`."

The graph models *user behavior*. Implementation is invisible — that's the point.

## 3. Pause between nodes

Every transition is a chance for the model to commit a node boundary. A brief pause + a fresh "Now I'm on the dashboard…" sentence cleanly separates nodes in the transcript.

## 4. Use the exact label you see on the page

> "I click **Continue with Google**."

Not:

> "I click the Google button."

The model uses the literal text you say as the `accessibleName` fingerprint. Saying the actual label means the resulting graph matches the live DOM, and drift detection works on day one.

## 5. Call out asserts explicitly

If the test should verify something, say so:

> "I should see a **Welcome back** heading."

The model treats verbs like *see*, *verify*, *expect* as assertion markers. Without them you get a transition graph with no asserts — which means the run will pass on a broken app.

## 6. Skip what you've already said

You don't need to re-narrate the login flow at the start of every recording. If `login` already exists as a flow, mention it once:

> "Run the **login** subflow, then I'm on the dashboard."

Cairn lifts it as a `subflow:` reference. See the [schema reference](/docs/schema-reference).

## 7. Wrap up with the exit condition

End each recording with what *done* looks like:

> "The flow ends when I see the **Order confirmed** heading."

That terminal node anchors the path and makes the LLM's job dramatically easier than open-ended trailing audio.

## When to fix YAML by hand

Recording is a fast path to a draft, not a contract. After the recording lands, open `.graphs/<flow>.yaml` and:

- Add `testId:` to asserts where you have stable test IDs in the code
- Add `probabilistic:` to asserts whose copy may change
- Add `inputs:` referencing fixtures or secrets instead of inline literals

`cairn validate` checks your edits against the schema before the next run.
